// Single call site for invoking the portal-notify edge function from
// the staff app. Wraps the supabase.functions.invoke pattern with the
// build-time feature flag, a uniform toast convention, and a normalized
// return shape so callers can keep their action flow linear.
//
// Toast convention (matches the dispatch's request for subtle staff
// feedback): on { sent: true } surface a success toast with the
// action-specific label ('Job completed · client notified'); on
// { skipped: true } stay silent (the common case for non-portal
// contacts); on { warning } surface a warning toast that doesn't
// obscure the underlying action's success toast.
//
// Failures NEVER throw — every call returns and the caller's action
// (the DB write that just succeeded) is unaffected. Errors are logged
// to console for our own visibility.

import { supabase } from '@/lib/supabase'
import { PORTAL_NOTIFICATIONS } from '@/lib/featureFlags'
import { toast } from 'sonner'

type PortalEvent = 'request_converted' | 'job_scheduled' | 'job_completed' | 'vendor_en_route'

interface NotifyResult { sent: boolean; skipped?: boolean; reason?: string; warning?: string }

const ACTION_LABEL: Record<PortalEvent, string> = {
  request_converted: 'Client notified',
  job_scheduled: 'Client notified · service scheduled',
  job_completed: 'Client notified · service completed',
  // vendor_en_route is normally fired by the scheduled-task processor (no
  // staff React call). If a future staff button hits portal-notify
  // directly, this label surfaces in the toast.
  vendor_en_route: 'Client notified · technician en route',
}

interface NotifyArgs { event: PortalEvent; job_id?: string; request_id?: string }

export async function notifyPortal(args: NotifyArgs): Promise<NotifyResult> {
  if (!PORTAL_NOTIFICATIONS) return { sent: false, skipped: true, reason: 'flag_off' }
  const { data, error } = await supabase.functions.invoke<NotifyResult>('portal-notify', { body: args })
  if (error) {
    console.warn(`portal-notify ${args.event} failed:`, error.message)
    return { sent: false, warning: error.message }
  }
  const result = data ?? { sent: false }
  if (result.sent) {
    toast.success(ACTION_LABEL[args.event])
  } else if (result.warning) {
    toast.warning(`Client email delayed — ${result.warning}`)
  }
  // Skipped (no portal access / no email): silent by design.
  return result
}
